export type Deadline = {
  hours: number;
  minutes: number;
  seconds: number;
};

export const convertDateToLocaleString = (date: string, locale = 'en-US'): string => {
  const dateObj = new Date(date);

  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const getDateInterval = ({
  locale = 'en',
  fromOffset = 0,
  toOffset = 1
}: {
  locale: string;
  fromOffset: number;
  toOffset: number;
}) => {
  const today = new Date();
  const from = new Date();
  const to = new Date();

  from.setDate(today.getDate() + fromOffset);
  if (from.getDay() === 0) {
    from.setDate(today.getDate() + fromOffset + 1);
  } else if (to.getDay() === 6) {
    from.setDate(today.getDate() + fromOffset + 2);
  }

  to.setDate(from.getDate() + toOffset);
  if (to.getDay() === 0) {
    to.setDate(from.getDate() + toOffset + 1);
  } else if (to.getDay() === 6) {
    to.setDate(from.getDate() + toOffset + 2);
  }

  if (from.getMonth() === to.getMonth()) {
    return from.toLocaleDateString(locale, { day: 'numeric', month: 'long' }) + '-' + to.getDate();
  } else {
    return (
      from.toLocaleDateString(locale, { day: 'numeric', month: 'long' }) +
      '-' +
      to.toLocaleDateString(locale, { day: 'numeric', month: 'long' })
    );
  }
};

const getHoursFromDiff = (diff: number) => {
  return Math.floor(diff / (1000 * 60 * 60));
};

const getMinuteFromDiff = (diff: number) => {
  return Math.floor((diff / (1000 * 60)) % 60);
};

const getSecondFromDiff = (diff: number) => {
  return Math.floor((diff / 1000) % 60);
};

// Get Deadline in days, hours, minutes, seconds
export const getDeadline = (deadline: string): Deadline => {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  const hours = getHoursFromDiff(diff);
  const minutes = getMinuteFromDiff(diff);
  const seconds = getSecondFromDiff(diff);

  // If deadline is passed, keep everything at 0
  if (hours < 0 || minutes < 0 || seconds < 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  return { hours, minutes, seconds };
};

// Get Deadline in hours, minutes, seconds
export const getDailyDeadline = (deadline: string): Deadline => {
  const deadlineDate = new Date();
  const deadlineArray = deadline.split(':');
  deadlineDate.setHours(Number(deadlineArray[0]));
  deadlineDate.setMinutes(Number(deadlineArray[1]));
  deadlineDate.setSeconds(0);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  const hours = getHoursFromDiff(diff);
  const minutes = getMinuteFromDiff(diff);
  const seconds = getSecondFromDiff(diff);

  // If deadline is passed, add 1 day to deadline
  if (hours < 0 || minutes < 0 || seconds < 0) {
    deadlineDate.setDate(deadlineDate.getDate() + 1);
    const diff = deadlineDate.getTime() - now.getTime();

    const hours = getHoursFromDiff(diff);
    const minutes = getMinuteFromDiff(diff);
    const seconds = getSecondFromDiff(diff);

    return { hours, minutes, seconds };
  }

  return { hours, minutes, seconds };
};

export const padNumber = (num = 0, length = 2): string => {
  return num.toString().padStart(length, '0');
};
